import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const comparisonRows = [
  {
    label: "Primary focus",
    cpo: "Product strategy, user experience, and product-led growth",
    cmo: "Brand strategy, demand generation, and market positioning",
  },
  {
    label: "Growth lever",
    cpo: "The product itself: activation, retention, expansion revenue",
    cmo: "Marketing channels: content, paid, events, partnerships",
  },
  {
    label: "Key metrics",
    cpo: "Activation rate, retention, NRR, product-qualified leads",
    cmo: "MQLs, pipeline, brand awareness, CAC",
  },
  {
    label: "Best for",
    cpo: "Companies where the product is the primary growth engine",
    cmo: "Companies where awareness and demand are the bottleneck",
  },
  {
    label: "How they collaborate",
    cpo: "Ensures the product delivers the value marketing promises",
    cmo: "Ensures the market knows about the value the product delivers",
  },
];

const faqs = [
  {
    question: "What is the difference between a CPO and a CMO?",
    answer:
      "A CPO (Chief Product Officer) drives growth through the product experience itself: what gets built, how users activate and retain, and how the product generates revenue. A CMO (Chief Marketing Officer) drives growth through market awareness, demand generation, and brand positioning. The CPO builds value; the CMO communicates it.",
  },
  {
    question: "Can a fractional CMO handle product strategy?",
    answer:
      "Product strategy requires deep expertise in user research, technical trade-offs, team structure, and product-led growth. While a CMO understands market positioning, product strategy is a distinct discipline. Companies that ask their CMO to own product often end up with marketing-driven features that don't improve retention or unit economics.",
  },
  {
    question:
      "Do I need FractionalCPO if I already have a fractional CMO?",
    answer:
      "Often, yes. If your marketing is generating demand but your product can't convert or retain those users, you have a product problem, not a marketing problem. FractionalCPO and a CMO are complementary. We ensure the product delivers on what marketing promises.",
  },
];

const FractionalCPOvsCMO = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="Fractional CPO vs Fractional CMO: Which Do You Need?"
        description="Understand the difference between a Fractional CPO and Fractional CMO. Learn which role drives growth for your technology company and when you need both."
        path="/fractional-cpo-vs-cmo"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Fractional CPO vs Fractional CMO: Which Do You Need?",
          "url": "https://fractionalcpo.com/fractional-cpo-vs-cmo",
          "dateModified": "2026-06-17",
          "description": "Understand the difference between a Fractional CPO and Fractional CMO. Learn which role drives growth for your technology company and when you need both.",
          "isPartOf": { "@type": "WebSite", "url": "https://fractionalcpo.com" }
        }}
      />

      {/* Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            Comparison
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-12"
          >
            Fractional CPO vs{" "}
            <span className="text-[#002178] font-medium">Fractional CMO</span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl"
          >
            <p className="text-xl md:text-2xl leading-relaxed font-medium text-[#1a1a1a] mb-6">
              Both roles drive growth, but through different levers.
            </p>
            <p className="text-lg text-black/60 leading-relaxed mb-4">
              A CPO drives growth through the product experience. Activation,
              retention, expansion revenue, and the product decisions that move
              those numbers.
            </p>
            <p className="text-lg text-black/60 leading-relaxed">
              A CMO drives growth through marketing and demand generation.
              Awareness, pipeline, positioning, and the channels that bring
              prospects to the door.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            How they{" "}
            <span className="text-[#002178] font-medium">compare</span>
          </motion.h2>

          {/* Desktop Table */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hidden md:block overflow-x-auto"
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-black/10">
                  <th className="text-left py-4 pr-6 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[20%]" />
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-[#002178] font-medium w-[40%]">
                    Fractional CPO
                  </th>
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[40%]">
                    Fractional CMO
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-black/5 hover:bg-black/[0.02] transition-colors"
                  >
                    <td className="py-5 pr-6 text-sm font-medium text-black/60">
                      {row.label}
                    </td>
                    <td className="py-5 px-4 text-sm font-medium text-[#1a1a1a]">
                      {row.cpo}
                    </td>
                    <td className="py-5 px-4 text-sm text-black/50">
                      {row.cmo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-6">
            {comparisonRows.map((row, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.08 }}
                className="border-b border-black/10 pb-6"
              >
                <p className="text-sm font-medium text-black/40 uppercase tracking-[0.15em] mb-3">
                  {row.label}
                </p>
                <div className="space-y-3">
                  <div className="bg-[#002178]/[0.04] p-3 rounded">
                    <p className="text-xs text-[#002178] font-medium mb-1">
                      Fractional CPO
                    </p>
                    <p className="text-sm font-medium">{row.cpo}</p>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-black/40 font-medium mb-1">
                      Fractional CMO
                    </p>
                    <p className="text-sm text-black/60">{row.cmo}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* When You Need a CPO, Not a CMO */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div>
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                When you need a CPO,{" "}
                <span className="text-[#002178] font-medium">not a CMO</span>
              </motion.h2>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="space-y-4"
              >
                <p className="text-lg text-black/60 leading-relaxed">
                  Marketing is doing its job. Leads are coming in. But
                  something is broken between "they signed up" and "they
                  became a loyal customer."
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  When the product can't convert or retain the users marketing
                  brings in, that's a product leadership gap. No amount of
                  demand generation fixes a leaky bucket.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  When the board says "product isn't driving growth," the
                  answer is rarely more marketing spend. It's senior product
                  leadership that can connect product decisions to business
                  outcomes.
                </p>
              </motion.div>
            </div>

            <div>
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                When you need{" "}
                <span className="text-[#002178] font-medium">both</span>
              </motion.h2>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="space-y-4"
              >
                <p className="text-lg text-black/60 leading-relaxed">
                  The CPO and CMO are natural partners. The CPO ensures the
                  product delivers value. The CMO ensures the market knows
                  about it.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  In PE-backed technology companies, this combination is
                  powerful. The CPO improves the product's conversion and
                  retention metrics while the CMO drives pipeline and
                  positioning. Together, they accelerate the path to revenue
                  growth.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  The key is clarity on who owns what. The CPO owns the
                  product experience. The CMO owns the market narrative. When
                  both are strong, growth compounds.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* How FractionalCPO Works */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
            >
              How{" "}
              <span className="font-medium">FractionalCPO</span> works
            </motion.h2>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-white/70 leading-relaxed mb-6"
            >
              We embed senior product leaders into your organization. Not
              advisors on the sideline. Embedded executives who own outcomes,
              coach your team, and drive measurable impact.
            </motion.p>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-white/70 leading-relaxed mb-8"
            >
              Every engagement starts with a{" "}
              <Link to="/assessment" className="text-white underline underline-offset-4 hover:text-white/90" onClick={() => trackEvent('internal_nav', { to: '/assessment', source: 'section_cta' })}>
                product assessment
              </Link>{" "}
              to identify the highest-impact opportunities. From there, we
              execute.
            </motion.p>

            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }}>
              <button
                onClick={() => { trackEvent('contact_click', { source: 'cta_block' }); setIsContactOpen(true); }}
                className="inline-block px-8 py-4 bg-white text-[#002178] font-medium hover:scale-105 transition-transform"
              >
                Let's chat
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default FractionalCPOvsCMO;
